import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components/macro";

import { atomicConfig } from "../../../config";
import { trackEvent } from "../../../utils/analytics";
import { call_api } from "../../../utils/services";
import { ReactComponent as Logo } from "../../../vendor/logo-dark.svg";

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

const Brand = styled(Logo)`
  width: 150px;
  height: 50px;
  margin-bottom: 20px;
`;

const LogoCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Maps backend error codes (and HTTP statuses) to user-facing messages.
export function mapAdsErrorToMessage(code, fallback) {
  switch (code) {
    case "missing_params":
      return "Missing required parameters from Amazon.";
    case "invalid_or_expired_state":
      return "Your Amazon authorization session expired. Please try connecting again.";
    case "unauthenticated":
      return "Authentication required to complete the connection.";
    case "token_exchange_failed":
      return "Failed to exchange the Amazon authorization code. Please try again.";
    case "access_token_failed":
      return "Failed to retrieve an Amazon access token. Please try again.";
    case "sp_api_not_connected":
      return "Please connect your Amazon seller account first.";
    case "no_matching_profile":
      return "No matching Amazon Ads profile was found for your seller account.";
    default:
      return fallback || "Failed to complete the Amazon Ads authorization.";
  }
}

function extractErrorCode(err) {
  const data = err?.response?.data;
  if (data && typeof data === "object") {
    return data.error || data.message || null;
  }
  return null;
}

function AmazonAdsCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (!code) {
        navigate(
          `/auth/amazon/error?message=${encodeURIComponent(
            "Missing authorization code from Amazon."
          )}`
        );
        return;
      }

      try {
        const url = `${atomicConfig.userServiceUrl}/amazon/ads/callback`;
        // No-auth call: the email-link path has no session yet, and the
        // in-dashboard path is authenticated server-side via `state`.
        const result = await call_api(url, "POST", { code, state });
        const body = result?.data || {};

        // Email-link path: backend stored the row and will reconcile it to
        // a user out-of-band (by selling_partner_id once the user has an
        // SP-API connection). We intentionally ignore body.temp_token - the
        // FE no longer redeems anything. From the user's perspective the
        // connection succeeded; treat it the same as the immediate path.
        if (body.pending === true) {
          trackEvent("amazon_ads_callback_pending");
          navigate("/auth/amazon/ads/success");
          return;
        }

        // Immediate-resolve path.
        if (Array.isArray(body.linked)) {
          trackEvent("amazon_ads_callback_success", {
            count: body.linked.length,
          });
          navigate("/auth/amazon/ads/success");
          return;
        }

        // Unexpected shape - treat as error.
        trackEvent("amazon_ads_callback_failed", { error: "unexpected_shape" });
        navigate(
          `/auth/amazon/error?message=${encodeURIComponent(
            "Unexpected response from server."
          )}`
        );
      } catch (err) {
        const code = extractErrorCode(err);
        trackEvent("amazon_ads_callback_failed", {
          error: code || err?.message || "unknown",
        });
        navigate(
          `/auth/amazon/error?message=${encodeURIComponent(
            mapAdsErrorToMessage(
              code,
              err?.message || "Failed to complete the Amazon Ads authorization."
            )
          )}`
        );
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <React.Fragment>
      <Wrapper>
        <LogoCenter>
          <Brand />
        </LogoCenter>
        <Helmet title="Connecting Amazon Ads..." />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <CircularProgress size={48} />

          <Typography component="h1" variant="h5" align="center">
            Connecting your Amazon Ads account...
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{ color: "text.secondary" }}
          >
            Please wait while we complete the authorization process.
          </Typography>
        </Box>
      </Wrapper>
    </React.Fragment>
  );
}

export default AmazonAdsCallback;
